/* eslint-disable no-undefined */
import {Text} from '@rdcomponents/text/text';
import {Space, Table} from 'antd';
import {SorterResult} from 'antd/es/table/interface';
import dayjs from 'dayjs';
import {decamelize} from 'humps';
import {Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo} from 'react';

import {PageSubHeader} from '../../../../../../layout/page-header/page-sub-header';
import {AsciiSpinner} from '../../../../../../layout/spinner/ascii-spinner';
import {Locale} from '../../../../../../provider/locale/localization';
import {MainFilterContext} from '../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../provider/main-filter/main-filter-type';
import {classNames} from '../../../../../../util/css';
import {usePagination} from '../../../../../../util/pagination-hook/pagination-hook';
import {saveNewSortTableState} from '../../../../../../util/save-sort-table-state/save-sort-table-state';
import {SavedSortStateType} from '../../../../../../util/save-sort-table-state/save-sort-table-type';
import {defaultCompetitorsTablePageSize} from '../competitors-page-const';
import {useCompetitorsTable} from '../competitors-page-hook';
import {CompetitorsType, PositionChangeEnum} from '../competitors-page-type';

import {addInitialSort, useCompetitorsColumnList} from './competitors-table-helper';
import {CompetitorsTableRowDataType} from './competitors-table-type';
import * as styles from './competitors-table.scss';

type PropsType = {
    positionChange: PositionChangeEnum | null;
    ordering: SavedSortStateType | null;
    setOrdering: Dispatch<SetStateAction<SavedSortStateType | null>>;
    routerLocation: string;
};

export function CompetitorsTable(props: PropsType): JSX.Element {
    const {ordering, setOrdering, routerLocation, positionChange} = props;
    const companyIsInProgress = false;

    const competitorsColumnList = useCompetitorsColumnList();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const paginationDependencies = useMemo(() => {
        return {mainFilterKey, positionChange};
    }, [mainFilterKey, positionChange]);

    const pagination = usePagination({
        dependencies: paginationDependencies,
        initialPageSize: defaultCompetitorsTablePageSize,
    });

    const {page, pageSize, onDataLoaded, onChange: onPaginationChange} = pagination;

    const competitorsOptions = useMemo((): {
        date: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        page: number;
        pageSize: number;
    } => {
        let sortOrderRequest: 'asc' | 'desc' | null = null;

        if (ordering?.order === 'ascend') {
            sortOrderRequest = 'asc';
        } else if (ordering?.order === 'descend') {
            sortOrderRequest = 'desc';
        }

        return {
            date: dayjs().format('YYYY-MM-DD'),
            sortBy: decamelize(ordering?.field || '') || 'weekly_extra_views',
            sortOrder: sortOrderRequest || 'desc',
            page,
            pageSize,
        };
    }, [ordering, page, pageSize]);

    const {mutate: fetchCompetitorsTable, isLoading: isInProgress, data: result, isSuccess} = useCompetitorsTable();

    useEffect(() => {
        if (isSuccess) {
            onDataLoaded({count: result.total});
        }
    }, [isSuccess, onDataLoaded, result]);

    useEffect(() => {
        fetchCompetitorsTable({
            filterKey: mainFilterKey || undefined,
            ...competitorsOptions,
            ...(positionChange ? {positionChange, comparedDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD')} : {}),
        });
    }, [fetchCompetitorsTable, mainFilterKey, competitorsOptions, positionChange]);

    function onSortingChange(
        sorter: SorterResult<CompetitorsTableRowDataType> | Array<SorterResult<CompetitorsTableRowDataType>>
    ) {
        if (Array.isArray(sorter) || !sorter.field || !sorter.order) {
            if (ordering === null) {
                return;
            }

            saveNewSortTableState(routerLocation, sorter);

            setOrdering(null);
        } else {
            setOrdering({
                order: sorter.order,
                field: sorter.field.toString(),
            });
            saveNewSortTableState(routerLocation, sorter);
        }
    }

    const convertCompetitorTableRow = useCallback((company: CompetitorsType): CompetitorsTableRowDataType => {
        const {
            name,
            code,
            addressCityName,
            addressAddsFormatted,
            targetRating,
            weeklyExtraViews,
            growthPotentialPercent,
            positionChange: companyPostionChange,
        } = company;

        return {
            name,
            code,
            city: addressCityName,
            address: addressAddsFormatted,
            growthPotentialPercent,
            targetRating,
            weeklyExtraViews,
            positionChange: companyPostionChange,
        };
    }, []);

    const tableDataSourceList = useMemo(() => {
        if (!result) {
            return [];
        }

        return result.items.map(convertCompetitorTableRow);
    }, [result, convertCompetitorTableRow]);

    return (
        <div className={styles.CompetitorsTable}>
            <PageSubHeader className={styles.CompetitorsTable_subHeader}>
                <Space size="large" wrap>
                    <Text>
                        <Locale
                            stringKey="TEXT__LIST_OF_COMPANIES"
                            valueMap={{
                                count: (
                                    <Text bolder id="companiesCount">
                                        {companyIsInProgress ? <AsciiSpinner /> : result?.total ?? 0}
                                    </Text>
                                ),
                            }}
                        />
                    </Text>
                </Space>
            </PageSubHeader>
            <Table
                columns={addInitialSort(competitorsColumnList, ordering)}
                dataSource={tableDataSourceList}
                loading={isInProgress}
                onChange={(newPagination, _filters, sorter) => {
                    const {current, pageSize: newPageSize} = newPagination;

                    onSortingChange(sorter);
                    onPaginationChange(current || 1, newPageSize);
                }}
                pagination={{
                    size: 'default',
                    showSizeChanger: true,
                    hideOnSinglePage: false,
                    current: page,
                    pageSize,
                    total: result?.total,
                }}
                rowClassName={(record) => {
                    return classNames({
                        [styles.CompetitorsTable__positionImprovedRow]:
                            record.positionChange === PositionChangeEnum.Improved,
                        [styles.CompetitorsTable__positionWorsenedRow]:
                            record.positionChange === PositionChangeEnum.Worsened,
                    });
                }}
                scroll={{x: true}}
            />
        </div>
    );
}
